"use client";

import type { ProductDetails } from "@ellaandil/formatters";
import { parseAsString, useQueryState } from "nuqs";
import { Suspense, useMemo } from "react";
import { Container } from "@/components/ui/Container";
import {
	RecommendationsCarousel,
	type RecommendedProduct,
} from "@/components/ui/sections/RecommendationsCarousel";
import { useTranslations } from "@/i18n/client";
import { getDepictVariantId } from "@/lib/depict/product-id";
import { useRecommendations } from "@/lib/depict/useRecommendations";

const RECOMMENDATIONS_LIMIT = 8;

type Items = ProductDetails["items"];
type Item = NonNullable<Items>[number];

export interface ProductRecommendationsProps {
	items: Items;
}

const inStock = (stock: Item["stock"]) => stock !== "no" && stock !== 0 && stock != null;

/**
 * Renders Depict's `related` recommendations below the PDP buy box — the
 * same carousel + inline quick-add used for the cart drawer's upsell (see
 * `CartUpsell`), just fed by the product currently being viewed instead of
 * the cart contents.
 */
export const ProductRecommendations = (props: ProductRecommendationsProps) => {
	return (
		<Suspense fallback={null}>
			<ProductRecommendationsInner {...props} />
		</Suspense>
	);
};

const ProductRecommendationsInner = ({ items }: ProductRecommendationsProps) => {
	const t = useTranslations();

	// `?size=` is meant to track the size on screen (see DepictView), but
	// nothing currently writes it to the URL — SizeSelector only keeps the
	// selection in local state — so it's `null` on a normal page load. Fall
	// back to the same "first in-stock size" SizeSelector auto-selects by
	// default, so recommendations still resolve on first render instead of
	// silently staying empty.
	const [urlSize] = useQueryState("size", parseAsString);
	const firstAvailableSize = useMemo(
		() => items?.find((item) => inStock(item.stock))?.name ?? items?.[0]?.name ?? null,
		[items],
	);
	const productId = getDepictVariantId(items, urlSize ?? firstAvailableSize);

	const { data } = useRecommendations({
		productIds: productId ? [productId] : [],
		type: "related",
		limit: RECOMMENDATIONS_LIMIT,
		enabled: Boolean(productId),
	});

	const products = (data?.products ?? []) as RecommendedProduct[];

	if (products.length === 0) return null;

	return (
		<Container as="section" className="py-grid-section-vertical-lg">
			<RecommendationsCarousel
				products={products}
				heading={t("You may also like")}
				layout="pair-quartets"
				headingVariant="heading-display-xs"
				cardSize="large"
				quickAdd={false}
			/>
		</Container>
	);
};
